package org.example.project.client.session

import io.ktor.client.HttpClient
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.time.Clock
import org.example.project.client.api.AuthApiClient
import org.example.project.client.api.LoginApiResponse
import org.example.project.client.config.ApiConfig
import org.example.project.client.error.MissingCsrfTokenException
import org.example.project.client.error.SessionStorageException
import org.example.project.client.error.UnauthorizedException
import org.example.project.client.model.ClientType
import org.example.project.client.model.LoginRequest
import org.example.project.client.model.RegisterRequest
import org.example.project.client.model.UserProfileResponse
import org.example.project.client.time.AppTime

interface SessionRepository {
    val sessionState: StateFlow<SessionState>
    val clientType: ClientType

    suspend fun restoreSession(): SessionState
    suspend fun register(request: RegisterRequest): UserProfileResponse
    suspend fun login(request: LoginRequest): SessionState
    suspend fun refreshMe(): UserProfileResponse
    suspend fun updateOwnProfile(displayName: String): UserProfileResponse
    suspend fun logout(): RemoteLogoutResult
    fun close()
}

internal class DefaultSessionRepository(
    private val authApiClient: AuthApiClient,
    private val sessionStorage: SessionStorage,
    private val apiConfig: ApiConfig,
    override val clientType: ClientType,
    private val clock: Clock = Clock.System,
    private val httpClientToClose: HttpClient? = null
) : SessionRepository {

    private val _sessionState = MutableStateFlow<SessionState>(SessionState.Unauthenticated)
    override val sessionState: StateFlow<SessionState> = _sessionState.asStateFlow()

    private val lifecycleMutex = Mutex()
    private var generation: Long = 0L

    override suspend fun restoreSession(): SessionState = lifecycleMutex.withLock {
        val currentGen = ++generation

        return when (clientType) {
            ClientType.ANDROID -> {
                val stored = try {
                    sessionStorage.load()
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Exception) {
                    null
                }

                if (stored !is StoredSession.Android || stored.baseUrl != apiConfig.canonicalOrigin) {
                    _sessionState.value = SessionState.Unauthenticated
                    clearStorageOrThrow()
                    return@withLock SessionState.Unauthenticated
                }

                val nowMillis = clock.now().toEpochMilliseconds()
                if (nowMillis >= stored.expiresAtMillis) {
                    _sessionState.value = SessionState.Unauthenticated
                    clearStorageOrThrow()
                    return@withLock SessionState.Unauthenticated
                }

                // Verify with remote server via GET /me
                val serverProfile = try {
                    authApiClient.getMe(stored.token)
                } catch (e: CancellationException) {
                    throw e
                } catch (e: UnauthorizedException) {
                    // Session is invalid or revoked on server
                    _sessionState.value = SessionState.Unauthenticated
                    clearStorageOrThrow()
                    return@withLock SessionState.Unauthenticated
                } catch (e: Exception) {
                    // Network error or 403: do NOT delete stored credentials, but rethrow so caller knows verification failed
                    throw e
                }

                if (generation != currentGen) {
                    return@withLock _sessionState.value
                }

                // Update stored profile with verified server profile
                try {
                    sessionStorage.save(stored.copy(user = serverProfile))
                } catch (e: CancellationException) {
                    throw e
                } catch (_: Exception) {}

                val state = SessionState.AuthenticatedAndroid(
                    token = stored.token,
                    expiresAtMillis = stored.expiresAtMillis,
                    user = serverProfile
                )
                _sessionState.value = state
                state
            }

            ClientType.WEB -> {
                val stored = try {
                    sessionStorage.load()
                } catch (e: CancellationException) {
                    throw e
                } catch (_: Exception) {
                    null
                }

                val storedWeb = if (stored is StoredSession.Web && stored.baseUrl == apiConfig.canonicalOrigin) {
                    val nowMillis = clock.now().toEpochMilliseconds()
                    if (nowMillis < stored.expiresAtMillis) stored else null
                } else {
                    null
                }

                // Attempt GET /me with browser HttpOnly cookie
                val serverProfile = try {
                    authApiClient.getMe(bearerToken = null)
                } catch (e: CancellationException) {
                    throw e
                } catch (e: UnauthorizedException) {
                    // Cookie missing or expired
                    _sessionState.value = SessionState.Unauthenticated
                    clearStorageOrThrow()
                    return@withLock SessionState.Unauthenticated
                } catch (e: Exception) {
                    throw e
                }

                if (generation != currentGen) {
                    return@withLock _sessionState.value
                }

                val state = if (storedWeb?.csrfToken != null) {
                    SessionState.AuthenticatedWeb(
                        csrfToken = storedWeb.csrfToken,
                        expiresAtMillis = storedWeb.expiresAtMillis,
                        user = serverProfile,
                        reauthenticationRequiredForMutation = false
                    )
                } else {
                    // Active cookie exists, but CSRF token is missing in this tab -> mutations require reauthentication
                    SessionState.AuthenticatedWeb(
                        csrfToken = null,
                        expiresAtMillis = Long.MAX_VALUE,
                        user = serverProfile,
                        reauthenticationRequiredForMutation = true
                    )
                }

                _sessionState.value = state
                state
            }
        }
    }

    override suspend fun register(request: RegisterRequest): UserProfileResponse {
        return authApiClient.register(request)
    }

    override suspend fun login(request: LoginRequest): SessionState = lifecycleMutex.withLock {
        require(request.clientType == clientType) {
            "LoginRequest clientType '${request.clientType}' does not match repository clientType '$clientType'"
        }

        val currentGen = ++generation
        _sessionState.value = SessionState.Unauthenticated
        try {
            sessionStorage.clear()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            throw SessionStorageException("Failed to clear previous session storage before login")
        }

        val apiResponse = try {
            authApiClient.login(request)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            throw e
        }

        val (newState, storedSession) = when (apiResponse) {
            is LoginApiResponse.Android -> {
                val expiresAtMillis = AppTime.parseIsoToEpochMillis(apiResponse.response.expiresAt)
                val stored = StoredSession.Android(
                    token = apiResponse.response.token,
                    expiresAtMillis = expiresAtMillis,
                    user = apiResponse.response.user,
                    baseUrl = apiConfig.canonicalOrigin
                )
                val state = SessionState.AuthenticatedAndroid(
                    token = apiResponse.response.token,
                    expiresAtMillis = expiresAtMillis,
                    user = apiResponse.response.user
                )
                state to stored
            }
            is LoginApiResponse.Web -> {
                val expiresAtMillis = AppTime.parseIsoToEpochMillis(apiResponse.response.expiresAt)
                val stored = StoredSession.Web(
                    csrfToken = apiResponse.response.csrfToken,
                    expiresAtMillis = expiresAtMillis,
                    baseUrl = apiConfig.canonicalOrigin
                )
                val state = SessionState.AuthenticatedWeb(
                    csrfToken = apiResponse.response.csrfToken,
                    expiresAtMillis = expiresAtMillis,
                    user = apiResponse.response.user,
                    reauthenticationRequiredForMutation = false
                )
                state to stored
            }
        }

        // Must successfully write to storage before declaring logged in!
        try {
            sessionStorage.save(storedSession)
        } catch (e: CancellationException) {
            try { sessionStorage.clear() } catch (ce: CancellationException) { throw ce } catch (_: Exception) {}
            throw e
        } catch (e: Exception) {
            val clearFailed = try {
                sessionStorage.clear()
                false
            } catch (ce: CancellationException) {
                throw ce
            } catch (_: Exception) {
                true
            }
            if (clearFailed) {
                throw SessionStorageException("Failed to save session credentials and failed to clear storage")
            }
            throw SessionStorageException("Failed to securely store session credentials")
        }

        if (generation == currentGen) {
            _sessionState.value = newState
        }
        newState
    }

    override suspend fun refreshMe(): UserProfileResponse {
        val (currentGen, token) = lifecycleMutex.withLock {
            val state = _sessionState.value
            val nowMillis = clock.now().toEpochMilliseconds()
            when (state) {
                is SessionState.AuthenticatedAndroid -> {
                    if (nowMillis >= state.expiresAtMillis) {
                        clearLocalSessionLocked()
                        throw UnauthorizedException(401, "UNAUTHORIZED", "Session expired", "client-expired")
                    }
                    generation to state.token
                }
                is SessionState.AuthenticatedWeb -> {
                    if (nowMillis >= state.expiresAtMillis) {
                        clearLocalSessionLocked()
                        throw UnauthorizedException(401, "UNAUTHORIZED", "Session expired", "client-expired")
                    }
                    generation to null
                }
                is SessionState.Unauthenticated -> {
                    throw UnauthorizedException(401, "UNAUTHORIZED", "No active session", "client-unauthenticated")
                }
            }
        }

        val updatedProfile = try {
            authApiClient.getMe(token)
        } catch (e: CancellationException) {
            throw e
        } catch (e: UnauthorizedException) {
            lifecycleMutex.withLock {
                if (generation == currentGen) {
                    clearLocalSessionLocked()
                }
            }
            throw e
        } catch (e: Exception) {
            throw e
        }

        lifecycleMutex.withLock {
            if (generation == currentGen) {
                when (val current = _sessionState.value) {
                    is SessionState.AuthenticatedAndroid -> {
                        _sessionState.value = current.copy(user = updatedProfile)
                        try {
                            sessionStorage.save(
                                StoredSession.Android(
                                    token = current.token,
                                    expiresAtMillis = current.expiresAtMillis,
                                    user = updatedProfile,
                                    baseUrl = apiConfig.canonicalOrigin
                                )
                            )
                        } catch (e: CancellationException) {
                            throw e
                        } catch (_: Exception) {}
                    }
                    is SessionState.AuthenticatedWeb -> {
                        _sessionState.value = current.copy(user = updatedProfile)
                    }
                    is SessionState.Unauthenticated -> {}
                }
            }
        }

        return updatedProfile
    }

    override suspend fun updateOwnProfile(displayName: String): UserProfileResponse {
        val (currentGen, token, csrf, isWeb) = lifecycleMutex.withLock {
            val nowMillis = clock.now().toEpochMilliseconds()
            when (val state = _sessionState.value) {
                is SessionState.AuthenticatedAndroid -> {
                    if (nowMillis >= state.expiresAtMillis) {
                        clearLocalSessionLocked()
                        throw UnauthorizedException(401, "UNAUTHORIZED", "Session expired", "client-expired")
                    }
                    Quadruple(generation, state.token, null, false)
                }
                is SessionState.AuthenticatedWeb -> {
                    if (nowMillis >= state.expiresAtMillis) {
                        clearLocalSessionLocked()
                        throw UnauthorizedException(401, "UNAUTHORIZED", "Session expired", "client-expired")
                    }
                    if (state.reauthenticationRequiredForMutation || state.csrfToken.isNullOrBlank()) {
                        throw MissingCsrfTokenException()
                    }
                    Quadruple(generation, null, state.csrfToken, true)
                }
                is SessionState.Unauthenticated -> {
                    throw UnauthorizedException(401, "UNAUTHORIZED", "No active session", "client-unauthenticated")
                }
            }
        }

        val updatedProfile = try {
            authApiClient.updateProfile(displayName, token, csrf, isWeb)
        } catch (e: CancellationException) {
            throw e
        } catch (e: UnauthorizedException) {
            lifecycleMutex.withLock {
                if (generation == currentGen) {
                    clearLocalSessionLocked()
                }
            }
            throw e
        } catch (e: Exception) {
            throw e
        }

        lifecycleMutex.withLock {
            if (generation == currentGen) {
                when (val current = _sessionState.value) {
                    is SessionState.AuthenticatedAndroid -> {
                        _sessionState.value = current.copy(user = updatedProfile)
                        try {
                            sessionStorage.save(
                                StoredSession.Android(
                                    token = current.token,
                                    expiresAtMillis = current.expiresAtMillis,
                                    user = updatedProfile,
                                    baseUrl = apiConfig.canonicalOrigin
                                )
                            )
                        } catch (e: CancellationException) {
                            throw e
                        } catch (_: Exception) {}
                    }
                    is SessionState.AuthenticatedWeb -> {
                        _sessionState.value = current.copy(user = updatedProfile)
                    }
                    is SessionState.Unauthenticated -> {}
                }
            }
        }

        return updatedProfile
    }

    override suspend fun logout(): RemoteLogoutResult = lifecycleMutex.withLock {
        val currentGen = ++generation
        val state = _sessionState.value

        if (state is SessionState.Unauthenticated) {
            return@withLock RemoteLogoutResult.LocalOnly("No active local session to terminate")
        }

        if (state is SessionState.AuthenticatedWeb && state.csrfToken.isNullOrBlank()) {
            val clearOk = try {
                sessionStorage.clear()
                true
            } catch (e: CancellationException) {
                throw e
            } catch (_: Exception) {
                false
            }
            _sessionState.value = SessionState.Unauthenticated
            return@withLock if (clearOk) {
                RemoteLogoutResult.LocalOnly("CSRF token missing; local session cleared")
            } else {
                RemoteLogoutResult.ClearFailed("CSRF token missing and failed to clear local storage")
            }
        }

        val token = (state as? SessionState.AuthenticatedAndroid)?.token
        val csrf = (state as? SessionState.AuthenticatedWeb)?.csrfToken
        val isWeb = state is SessionState.AuthenticatedWeb

        val remoteOk = try {
            authApiClient.logout(token, csrf, isWeb)
            true
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            false
        }

        val clearOk = try {
            sessionStorage.clear()
            true
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            false
        }

        _sessionState.value = SessionState.Unauthenticated

        if (!clearOk) {
            return@withLock RemoteLogoutResult.ClearFailed("Failed to clear local session storage")
        }

        return@withLock if (remoteOk) {
            RemoteLogoutResult.Success
        } else {
            RemoteLogoutResult.LocalOnly("Remote logout failed; local session cleared")
        }
    }

    private suspend fun clearLocalSessionLocked() {
        generation++
        _sessionState.value = SessionState.Unauthenticated
        clearStorageOrThrow()
    }

    private suspend fun clearStorageOrThrow() {
        try {
            sessionStorage.clear()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            throw SessionStorageException("Failed to clear session storage")
        }
    }

    override fun close() {
        httpClientToClose?.close()
    }

    private data class Quadruple<A, B, C, D>(val first: A, val second: B, val third: C, val fourth: D)
}
